import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { paymentsService } from '../../services/payments'

/**
 * Компонент оплаты через T-Bank
 * Поддерживает два режима:
 * 1. Редирект на платежную форму банка (для paymentUrl)
 * 2. Отображение QR-кода для оплаты через СБП (для qrData)
 * 
 * Документация: https://developer.tbank.ru/eacq/intro/connection
 */
const TBankPayment = ({ 
  paymentId,
  paymentUrl,
  orderId,
  amount, 
  qrData,
  onSuccess, 
  onError, 
  onCancel 
}) => {
  const navigate = useNavigate()
  const [redirecting, setRedirecting] = useState(false)
  const pollingRef = useRef(null)

  // Polling для проверки статуса оплаты при использовании QR-кода
  useEffect(() => {
    if (qrData && orderId) {
      // Запускаем polling каждые 3 секунды для проверки статуса
      pollingRef.current = setInterval(async () => {
        try {
          const response = await paymentsService.checkStatusByOrder(orderId)
          
          // Останавливаем polling при любом финальном статусе
          if (response.status === 'succeeded' || response.status === 'failed' || response.status === 'cancelled') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current)
              pollingRef.current = null
            }
            
            // Вызываем соответствующий callback
            if (response.status === 'succeeded') {
              onSuccess && onSuccess()
            } else if (response.status === 'failed') {
              onError && onError({ message: 'Платеж не прошел' })
            } else if (response.status === 'cancelled') {
              onCancel && onCancel()
      }
    }
        } catch (err) {
          console.log('Ошибка проверки статуса оплаты:', err.message)
        }
      }, 3000)

      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
        }
      }
    }
  }, [qrData, orderId, onSuccess, onError, onCancel])

  // Редирект на форму оплаты (если есть paymentUrl и нет qrData)
  useEffect(() => {
    if (paymentUrl && !qrData) {
      setRedirecting(true)
      // Сохраняем информацию о заказе в sessionStorage для очистки корзины после возврата
      if (orderId) {
        sessionStorage.setItem('pendingOrderId', orderId.toString())
        sessionStorage.setItem('shouldClearCart', 'true')
      }
      window.location.href = paymentUrl
    }
  }, [paymentUrl, qrData, orderId])

  // Если есть QR-код, показываем его
  if (qrData) {
    return (
      <div className="tbank-container" style={{ textAlign: 'center', padding: '32px 16px' }}>
        <div style={{
          fontSize: '16px',
          fontWeight: '600',
          marginBottom: '24px',
          color: '#000'
        }}>
          Отсканируйте QR-код для оплаты
        </div>
        
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '24px',
          padding: '24px',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <QRCodeSVG 
            value={qrData}
            size={256}
            level="H"
            includeMargin={true}
          />
        </div>

        <div style={{
          fontSize: '14px',
          color: '#666',
          lineHeight: '1.6',
          marginBottom: '16px'
        }}>
          Откройте приложение банка и отсканируйте QR-код
        </div>

        <div style={{
          fontSize: '12px',
          color: '#999',
          marginTop: '24px',
          padding: '12px',
          background: '#f9f9f9',
          borderRadius: '8px'
        }}>
          <div style={{ marginBottom: '8px' }}>💡 Сумма к оплате: <strong>{amount?.toLocaleString('ru-RU')} ₽</strong></div>
          <div>Ожидание подтверждения оплаты...</div>
        </div>

        {onCancel && (
          <button
            onClick={() => {
              if (pollingRef.current) {
                clearInterval(pollingRef.current)
                pollingRef.current = null
              }
              onCancel()
            }}
            style={{
              marginTop: '24px',
              padding: '12px 24px',
              background: 'transparent',
              border: '1px solid #ddd',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#666'
            }}
          >
            Отменить оплату
          </button>
        )}
      </div>
    )
  }

  // Если нет QR-кода, но есть paymentUrl - выполняем редирект
  if (paymentUrl && !qrData) {
  return (
      <div className="tbank-container" style={{ textAlign: 'center', padding: '48px 16px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #333',
              borderTopColor: '#ffdd2d',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
          margin: '0 auto 24px'
            }} />
            <div style={{ 
          fontSize: '16px', 
          fontWeight: '500',
          color: '#333',
          marginBottom: '8px'
            }}>
          Перенаправление на форму оплаты...
            </div>
        <div style={{ 
          fontSize: '14px', 
          color: '#666',
          lineHeight: '1.5'
        }}>
          Если перенаправление не произошло автоматически,{' '}
          <a href={paymentUrl} style={{ color: '#ffdd2d', textDecoration: 'underline' }}>
            нажмите здесь
          </a>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    )
  }

  // Если нет ни QR-кода, ни paymentUrl
  return (
    <div className="checkout-error" style={{ textAlign: 'center', padding: '32px' }}>
      Ошибка: не получены данные для оплаты
    </div>
  )
}

export default TBankPayment
